import type { $Enums } from '@prisma/client';
import { z } from 'zod';

import type { Expect, Mirrors } from './mirrors';

/**
 * Mirrors the `AccessAction` enum in `apps/work-permit/prisma/schema.prisma`,
 * in the same order. A value added there and not here fails the parity check
 * at the bottom of this file; see docs/adr/0013.
 */
export const AccessActionSchema = z.enum([
  'SYSTEM_CONFIG_UPDATE',
  'SYSTEM_AUDIT',
  'SYSTEM_MANAGE_SEEDS',
  'TENANT_CREATE',
  'TENANT_LIST',
  'TENANT_GET',
  'TENANT_UPDATE',
  'TENANT_DELETE',
  'ORG_CREATE',
  'ORG_MEMBER',
  'ORG_LIST',
  'ORG_GET',
  'ORG_UPDATE',
  'ORG_DELETE',
  'ORG_ASSIGN_SUBSCRIPTION',
  'ORG_USERS_INVITE',
  'ORG_USERS_MANAGE',
  'ORG_LIST_ALL_LOCATIONS',
  'ORG_LIST_ASSIGNED_LOCATIONS',
  'ORG_LIST_ALL_WORKFLOW_TYPES',
  'ORG_LIST_ASSIGNED_WORKFLOW_TYPES',
  'ORG_GET_STRUCTURE',
  'ORG_LIST_USERS',
  'ORG_LIST_ROLES',
  'VIEW_CHILD_ORGANISATION_PERMITS',
  'USER_CREATE',
  'USER_LIST',
  'USER_GET',
  'USERS_LIST_VIEW',
  'USER_UPDATE',
  'USER_DELETE',
  'USER_ASSIGN_ROLE',
  'USER_ASSIGN_ORG',
  'USER_LIST_UNASSIGNED',
  'USER_LIST_USER_ORGS',
  'USER_DELETE_UNASSIGNED',
  'USER_CONFIGURATION_CREATE',
  'USER_CONFIGURATION_LIST',
  'USER_CONFIGURATION_GET',
  'USER_CONFIGURATION_UPDATE',
  'USER_CONFIGURATION_DELETE',
  'MANAGE_ROLES',
  'ROLE_CREATE',
  'ADD_ROLE_RESTRICTION_CHILD_ORG',
  'ROLE_LIST',
  'ROLE_GET',
  'ROLE_UPDATE',
  'ROLE_DELETE',
  'ASSIGN_ROLES',
  'ASSIGN_WORKFLOW_ROLES',
  'MANAGE_WORKFLOWS',
  'WORKFLOW_CREATE',
  'WORKFLOW_LIST',
  'WORKFLOW_GET',
  'WORKFLOW_UPDATE',
  'WORKFLOW_DELETE',
  'WORKFLOW_TYPE_CREATE',
  'WORKFLOW_TYPE_LIST',
  'WORKFLOW_TYPE_GET',
  'WORKFLOW_TYPE_UPDATE',
  'WORKFLOW_TYPE_DELETE',
  'WORKFLOW_INSTANCE_LIST',
  'WORKFLOW_INSTANCE_GET',
  'WORKFLOW_INSTANCE_UPDATE',
  'WORKFLOW_INSTANCE_DELETE',
  'WORKFLOW_CREATE_PERMIT',
  'WORKFLOW_READ_PERMIT',
  'WORKFLOW_READ_ALL_ORG_PERMITS',
  'WORKFLOW_READ_ALL_TENANT_PERMITS',
  'WORKFLOW_READ_ALL_SUB_ORGS_PERMITS',
  'WORKFLOW_PROGRESS_PERMIT',
  'WORKFLOW_AUTHORIZE_PERMIT',
  'WORKFLOW_ISSUE_PERMIT',
  'WORKFLOW_ACTIVE_PERMIT_ACTIONS',
  'WORKFLOW_APPROVE_PERMIT_EXTENSION',
  'WORKFLOW_REQUEST_HIGHER_APPROVAL',
  'WORKFLOW_APPROVE_HIGHER_APPROVAL_REQUEST',
  'WORKFLOW_APPROVE_COUNTER_SIGNATURE',
  'WORKFLOW_SUBMIT_DESCRIPTION',
  'WORKFLOW_ASSIGN_ISSUER',
  'WORKFLOW_REASSIGN_ISSUER',
  'WORKFLOW_AUTO_ASSIGN_ISSUER',
  'WORKFLOW_ASSIGN_RECEIVER',
  'WORKFLOW_REASSIGN_RECEIVER',
  'WORKFLOW_AUTO_ASSIGN_RECEIVER',
  'WORKFLOW_ADD_PRE_WORK_GAS_RESULTS',
  'WORKFLOW_GAS_TESTS_REVIEW',
  'WORKFLOW_RISK_ACKNOWLEDGEMENT',
  'WORKFLOW_IN_ISSUER_LIST',
  'WORKFLOW_IN_RECEIVER_LIST',
  'WORKFLOW_IN_DESIGNATED_REPRESENTATIVE_LIST',
  'WORKFLOW_APPROVE_JSI_REQUEST',
  'WORKFLOW_COMPLETE_JSI',
  'WORKFLOW_ROLE_ISSUER',
  'WORKFLOW_ROLE_RECEIVER',
  'WORKFLOW_ROLE_GAS_TESTER',
  'WORKFLOW_ROLE_PRE_WORK_GAS_TESTER',
  'WORKFLOW_ROLE_INTERIM_WORK_GAS_TESTER',
  'WORKFLOW_ROLE_POST_WORK_GAS_TESTER',
  'WORKFLOW_FORM_WORK_TYPE',
  'WORKFLOW_FORM_WORK_DESCRIPTION',
  'WORKFLOW_FORM_WORK_DESCRIPTION_REVIEW',
  'WORKFLOW_FORM_HAZARD_IDENTIFICATION',
  'WORKFLOW_FORM_SUPPLEMENTARY_DOCUMENTS',
  'WORKFLOW_FORM_SCHEDULE_SITE_INSPECTION',
  'WORKFLOW_FORM_SCHEDULE_SITE_VISIT',
  'WORKFLOW_FORM_ACKNOWLEDGE_SCHEDULE_SITE_VISIT',
  'WORKFLOW_FORM_GAS_TESTING',
  'WORKFLOW_FORM_REVIEW_GAS_TESTS',
  'WORKFLOW_FORM_JSI',
  'WORKFLOW_FORM_PERMIT_ACKNOWLEDGEMENT',
  'WORKFLOW_FORM_ISSUE_PERMIT',
  'WORKFLOW_FORM_WORK_ON_SITE',
  'WORKFLOW_FORM_POST_WORK_INSPECTION',
  'WORKFLOW_FORM_HIGHER_APPROVAL_REQUEST',
  'WORKFLOW_FORM_COUNTER_SIGNATURE_APPROVAL',
  'WORKFLOW_FORM_COUNTER_SIGNATURE_MANAGE_REQUEST',
  'WORKFLOW_FORM_RENEW',
  'JSA_TASK_CREATE',
  'JSA_TASK_LIST',
  'JSA_TASK_GET',
  'JSA_TASK_UPDATE',
  'JSA_TASK_DELETE',
  'JSA_ACTIVITY_CREATE',
  'JSA_ACTIVITY_LIST',
  'JSA_ACTIVITY_GET',
  'JSA_ACTIVITY_UPDATE',
  'JSA_ACTIVITY_DELETE',
  'WORKFLOW_CREATE_JSA',
  'WORKFLOW_READ_JSA',
  'WORKFLOW_LIST_JSA',
  'WORKFLOW_VIEW_JSA_DASHBOARD',
  'WORKFLOW_EDIT_JSA_TASK',
  'WORKFLOW_DELETE_JSA_TASK',
  'WORKFLOW_RETURN_JSA',
  'WORKFLOW_APPROVE_JSA',
  'WORKFLOW_REJECT_JSA',
  'WORKFLOW_ASSIGN_JSA_LEAD',
  'WORKFLOW_ADD_JSA_TEAM_MEMBER',
  'WORKFLOW_SUBMIT_JSA_TEAM',
  'WORKFLOW_CREATE_JSA_TASK',
  'WORKFLOW_SUBMIT_JSA_TASKS',
  'WORKFLOW_ADD_JSA_APPROVER',
  'WORKFLOW_NOTIFY_JSA_APPROVERS',
  'WORKFLOW_PRINT_JSA',
  'WORKFLOW_ROLE_JSA_LEAD',
  'WORKFLOW_ROLE_JSA_TEAM_MEMBER',
  'WORKFLOW_ROLE_JSA_FOREMAN',
  'WORKFLOW_ROLE_JSA_APPROVER',
  'WORKFLOW_ROLE_JSA_REQUESTER',
  'WORKFLOW_FORM_JSA_GENERAL_INFORMATION',
  'WORKFLOW_FORM_JSA_ASSIGN_JSA_TEAM',
  'WORKFLOW_FORM_JSA_TASKS_BREAKDOWN',
  'WORKFLOW_FORM_JSA_APPROVALS',
  'WORKFLOW_FORM_JSA_APPROVE',
  'WORKFLOW_FORM_JSA_SUMMARY',
  'IP_REQUEST_CREATE',
  'IP_REQUEST_READ',
  'IP_REQUEST_APPROVE',
  'WORKFLOW_IP_REQUEST_APPROVE',
  'IP_PROCESS_CREATE',
  'IP_PROCESS_READ',
  'IP_VERIFICATION_CHECKLIST',
  'IP_EXECUTION_READ',
  'WORKFLOW_IP_EXECUTION_START',
  'WORKFLOW_IP_EXECUTION_SCHEDULE',
  'WORKFLOW_IP_EXECUTION_TASK_COMPLETE',
  'WORKFLOW_IP_EXECUTION_RECORD_DEVIATION',
  'WORKFLOW_IP_EXECUTION_REOPEN_TASK',
  'WORKFLOW_IP_EXECUTION_RELEASE_HOLD_POINT',
  'WORKFLOW_IP_EXECUTION_CERTIFY',
  'WORKFLOW_IP_EXECUTION_REJECT_CERTIFICATION',
  'WORKFLOW_IP_EXECUTION_CONFIRM_WORK_COMPLETE',
  'WORKFLOW_IP_EXECUTION_SUSPEND_CANCEL',
  'IP_EXECUTION_EXPORT_AUDIT',
  'OA_VIEW',
  'OA_EDIT',
  'OA_OPERATOR',
  'OA_SHIFT_SUPERVISOR',
  'OA_PLANT_MANAGER',
  'OA_LOGBOOK_CREATE',
  'OA_LOGBOOK_SUBMIT',
  'OA_LOGBOOK_AMEND',
  'OA_HANDOVER_INITIATE',
  'OA_HANDOVER_ACKNOWLEDGE',
  'WORKFLOW_CREATE_HEALTHCHECK',
  'WORKFLOW_READ_HEALTHCHECK',
  'WORKFLOW_FORM_HEALTHCHECK_STEP_1',
  'WORKFLOW_FORM_HEALTHCHECK_STEP_2',
  'WORKFLOW_FORM_HEALTHCHECK_STEP_3',
  'AUDIT_VIEW',
  'AUDIT_EDIT',
  'AUDIT_LEAD',
  'AUDIT_TEAM',
  'AUDIT_LIST',
  'MODULE_CREATE',
  'MODULE_LIST',
  'MODULE_VIEW',
  'MODULE_GET',
  'MODULE_UPDATE',
  'MODULE_REFERENCE_DOCUMENT_MANAGE',
  'MODULE_DELETE',
  'MODULE_CONFIG_CREATE',
  'MODULE_CONFIG_LIST',
  'MODULE_CONFIG_GET',
  'MODULE_CONFIG_UPDATE',
  'MODULE_CONFIG_DELETE',
  'SITE_CREATE',
  'SITE_BULK_UPLOAD',
  'LOCATION_LIST',
  'LOCATION_GET',
  'LOCATION_UPDATE',
  'LOCATION_DELETE',
  'LOCATION_REGION_CREATE',
  'LOCATION_REGION_LIST',
  'LOCATION_REGION_GET',
  'LOCATION_REGION_UPDATE',
  'LOCATION_REGION_DELETE',
  'LOCATION_SHIFT_CREATE',
  'LOCATION_SHIFT_LIST',
  'LOCATION_SHIFT_GET',
  'LOCATION_SHIFT_UPDATE',
  'LOCATION_SHIFT_DELETE',
  'EXPORT_LOCATION',
  'CERTIFICATE_CREATE',
  'CERTIFICATE_LIST',
  'CERTIFICATE_GET',
  'CERTIFICATE_UPDATE',
  'CERTIFICATE_DELETE',
  'CERTIFICATE_TYPE_CREATE',
  'CERTIFICATE_TYPE_LIST',
  'CERTIFICATE_TYPE_GET',
  'CERTIFICATE_TYPE_UPDATE',
  'CERTIFICATE_TYPE_DELETE',
  'DASHBOARD_VIEW',
  'DASHBOARD_CREATE',
  'DASHBOARD_LIST',
  'DASHBOARD_GET',
  'DASHBOARD_UPDATE',
  'DASHBOARD_DELETE',
  'DASHBOARD_TILE_CREATE',
  'DASHBOARD_TILE_LIST',
  'DASHBOARD_TILE_GET',
  'DASHBOARD_TILE_UPDATE',
  'DASHBOARD_TILE_DELETE',
  'DASHBOARD_DATA_SOURCE_CREATE',
  'DASHBOARD_DATA_SOURCE_LIST',
  'DASHBOARD_DATA_SOURCE_GET',
  'DASHBOARD_DATA_SOURCE_UPDATE',
  'DASHBOARD_DATA_SOURCE_DELETE',
  'EXPORT_KPI_DASHBOARD',
  'SUBSCRIPTION_CREATE',
  'SUBSCRIPTION_LIST',
  'SUBSCRIPTION_GET',
  'SUBSCRIPTION_UPDATE',
  'SUBSCRIPTION_DELETE',
  'FEATURE_CREATE',
  'FEATURE_LIST',
  'FEATURE_GET',
  'FEATURE_UPDATE',
  'FEATURE_DELETE',
  'LOG_LIST',
  'LOG_GET',
  'LOG_DELETE',
  'NOTIFICATION_NEW_WORK_PERMIT_REQUEST',
  'NOTIFICATION_NEW_WORK_PERMIT_REQUEST_RECEIVER',
  'NOTIFICATION_PERMIT_RETURNED',
  'NOTIFICATION_RETURNED_PERMIT_RESUBMITTED',
  'NOTIFICATION_INVITATION_FOR_JOINT_SITE_VISIT',
  'NOTIFICATION_JOINT_SITE_VISIT_IN_10_MINUTES',
  'NOTIFICATION_GAS_TESTING_COMPLETE',
  'NOTIFICATION_REQUEST_FOR_HIGHER_APPROVAL',
  'NOTIFICATION_HIGHER_APPROVAL_RECEIVED',
  'NOTIFICATION_HIGHER_APPROVAL_REJECTED',
  'NOTIFICATION_REQUEST_FOR_COUNTERSIGNATURE',
  'NOTIFICATION_COUNTERSIGNATURE_RECEIVED',
  'NOTIFICATION_COUNTERSIGNATURE_REJECTED',
  'NOTIFICATION_PERMIT_AUTHORISED',
  'NOTIFICATION_PERMIT_PAUSED',
  'NOTIFICATION_PERMIT_TERMINATED',
  'NOTIFICATION_REQUEST_TO_CLOSE_PERMIT',
  'NOTIFICATION_PERMIT_CLOSED',
  'NOTIFICATION_REQUEST_TO_RENEW_PERMIT',
  'NOTIFICATION_PERMIT_RENEWED',
  'NOTIFICATION_PERMIT_REQUEST_ASSIGNED_TO_YOU',
  'NOTIFICATION_JSA_NEW_REQUEST',
  'NOTIFICATION_JSA_RETURNED',
  'NOTIFICATION_JSA_RETURNED_JSA_RESUBMITTED',
  'NOTIFICATION_JSA_INVITATION_FOR_JSA_TEAM',
  'NOTIFICATION_JSA_REQUEST_FOR_APPROVAL',
  'NOTIFICATION_JSA_APPROVAL_RECEIVED',
  'NOTIFICATION_JSA_APPROVAL_REJECTED',
  'NOTIFICATION_JSA_APPROVED',
  'NOTIFICATION_JSA_LEAD_ASSIGNED',
  'NOTIFICATION_JSA_TEAM_ASSIGNED',
  'NOTIFICATION_JSA_TASKS_SUBMITTED',
  'NOTIFICATION_PERMIT_ESCALATION',
  'FILE_UPLOAD',
  'FILE_DELETE',
  'FILE_GET',
  'NOTIFICATION_VIEW',
  'NOTIFICATION_LIST',
  'NOTIFICATION_READ',
  'WORKFLOW_INSTANCE_APPROVAL_VIEW',
  'WORKFLOW_INSTANCE_APPROVAL_LIST',
  'PERMIT_MONITORING',
]);

export type AccessActionType = z.infer<typeof AccessActionSchema>;

export type AccessActionMirrorsPrisma = Expect<
  Mirrors<AccessActionType, $Enums.AccessAction>
>;
